import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ArrowRight, Megaphone, Trophy, MessageCircle, Sparkles, Download, Crown, Swords } from "lucide-react";
import { api, resolveImage } from "@/lib/api";
import { useSettings } from "@/hooks/useSettings";
import { ResellerCard } from "@/components/ResellerCard";
import { AgencyPodium } from "@/components/AgencyPodium";

function BannerCarousel({ banners }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    const t = setInterval(() => embla.scrollNext(), 5000);
    return () => clearInterval(t);
  }, [embla]);

  const scrollTo = useCallback((i) => embla && embla.scrollTo(i), [embla]);

  if (!banners?.length) return null;

  return (
    <div className="relative overflow-hidden rounded-3xl gold-border emerald-glow" data-testid="home-banner-carousel">
      <div ref={emblaRef}>
        <div className="flex">
          {banners.map((b) => {
            const isExternal = b.link && /^https?:\/\//i.test(b.link);
            const go = b.link
              ? () => (isExternal ? window.open(b.link, "_blank", "noopener,noreferrer") : navigate(b.link))
              : undefined;
            return (
              <div key={b.id} className="relative min-w-0 flex-[0_0_100%]">
                <div
                  onClick={go}
                  role={b.link ? "link" : undefined}
                  data-testid={`banner-link-${b.id}`}
                  className={`block ${b.link ? "cursor-pointer" : ""}`}
                >
                  <div className="relative h-[320px] sm:h-[420px] lg:h-[520px]">
                    {b.image_url && (
                      <img src={resolveImage(b.image_url)} alt={b.title} className="h-full w-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 lg:p-14">
                      {b.title && (
                        <h2 className="font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
                          {b.title}
                        </h2>
                      )}
                      {b.description && (
                        <p className="mt-3 max-w-xl text-sm text-slate-200 sm:text-base">{b.description}</p>
                      )}
                      {b.button_text && (
                        <span className="mt-5 inline-flex items-center gap-2 rounded-full gold-gradient px-6 py-3 text-sm font-bold text-obsidian">
                          {b.button_text} <ArrowRight className="h-4 w-4" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {banners.length > 1 && (
        <>
          <button onClick={() => embla && embla.scrollPrev()} className="absolute left-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-obsidian/60 p-2 text-gold backdrop-blur hover:bg-obsidian sm:block">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button onClick={() => embla && embla.scrollNext()} className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-obsidian/60 p-2 text-gold backdrop-blur hover:bg-obsidian sm:block">
            <ChevronRight className="h-6 w-6" />
          </button>
          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
            {banners.map((_, i) => (
              <button key={i} onClick={() => scrollTo(i)}
                className={`h-2 rounded-full transition-all ${i === selected ? "w-6 bg-gold" : "w-2 bg-white/40"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SectionHeading({ eyebrow, title, to, linkText }) {
  return (
    <div className="mb-8 flex items-end justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">{eyebrow}</p>
        <h2 className="mt-2 font-display text-2xl font-bold text-white sm:text-3xl lg:text-4xl">{title}</h2>
      </div>
      {to && (
        <Link to={to} className="hidden items-center gap-1 text-sm font-semibold text-gold hover:gap-2 sm:flex">
          {linkText} <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const settings = useSettings();
  const { data: banners } = useQuery({ queryKey: ["banners"], queryFn: async () => (await api.get("/banners")).data });
  const { data: notices } = useQuery({ queryKey: ["notices"], queryFn: async () => (await api.get("/notices")).data });
  const { data: ranking } = useQuery({ queryKey: ["ranking-latest"], queryFn: async () => (await api.get("/rankings/published")).data });
  const { data: resellers } = useQuery({ queryKey: ["resellers-preview"], queryFn: async () => (await api.get("/resellers")).data });

  return (
    <div>
      {/* Hero */}
      <section className="hero-radial">
        <div className="mx-auto max-w-7xl px-4 pb-4 pt-10 sm:px-6 sm:pt-14 lg:px-12">
          <div className="mb-8 text-center animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-4 py-1.5 text-xs font-semibold text-gold">
              <Sparkles className="h-3.5 w-3.5" /> {settings.site_title || "Golden Live"} Official Website
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl">
              Go Live. Shine Bright. <span className="gold-text">Recharge Instantly.</span>
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-slate-300">
              {settings.description || "The premium destination for live entertainment. Recharge safely through our trusted resellers."}
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/resellers" data-testid="hero-recharge-btn"
                className="flex h-12 w-full items-center justify-center gap-2 rounded-full gold-gradient px-8 text-base font-bold text-obsidian transition-transform hover:scale-[1.03] sm:w-auto">
                <MessageCircle className="h-5 w-5" /> Recharge via Reseller
              </Link>
              {settings.app_download_link ? (
                <a href={settings.app_download_link} target="_blank" rel="noreferrer" data-testid="hero-download-btn"
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-gold/40 px-8 text-base font-bold text-gold hover:bg-gold/10 sm:w-auto">
                  <Download className="h-5 w-5" /> Download App
                </a>
              ) : (
                <Link to="/official-host" data-testid="hero-download-btn"
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-gold/40 px-8 text-base font-bold text-gold hover:bg-gold/10 sm:w-auto">
                  <Crown className="h-5 w-5" /> Become a Host
                </Link>
              )}
            </div>
          </div>
          <BannerCarousel banners={banners} />
        </div>
        <div className="mx-auto max-w-5xl px-4 sm:px-6"><div className="section-divider" /></div>
      </section>

      {/* PK Battle banner card */}
      <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-12">
        <Link to="/pk-battles" data-testid="pk-battle-banner"
          className="card-surface group relative flex items-center justify-between gap-4 overflow-hidden rounded-2xl p-6 transition-all hover:-translate-y-1 hover:gold-glow sm:p-8">
          <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald/70 via-gold/60 to-emerald/70" />
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl gold-gradient gold-glow">
              <Swords className="h-7 w-7 text-obsidian" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-light">Live Showdown</p>
              <h2 className="mt-1 font-display text-xl font-bold text-white sm:text-2xl">Upcoming PK Battles</h2>
              <p className="mt-1 text-sm text-slate-400">See which hosts go head-to-head next.</p>
            </div>
          </div>
          <span className="hidden shrink-0 items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-sm font-bold text-gold transition-colors group-hover:bg-gold/10 sm:inline-flex">
            View Battles <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
      </section>

      {/* Notices */}
      {notices?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-12">
          <SectionHeading eyebrow="Stay Updated" title="Important Notices" to="/notices" linkText="View all" />
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {notices.slice(0, 3).map((n) => (
              <div key={n.id} className="card-surface relative overflow-hidden rounded-2xl p-6 transition-all hover:-translate-y-1" data-testid={`home-notice-${n.id}`}>
                <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald/70 to-gold/50" />
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-gold" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-gold">{n.category}</span>
                </div>
                <h3 className="mt-3 text-lg font-bold text-slate-100">{n.title}</h3>
                <p className="mt-2 line-clamp-3 text-sm text-slate-400">{n.description}</p>
                {n.date && <p className="mt-3 text-xs text-slate-500">{n.date}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Top Agencies */}
      {ranking?.agencies?.length > 0 && (
        <section className="relative border-y border-emerald/25 bg-gradient-to-b from-emerald-dark/45 via-emerald-dark/15 to-transparent py-14 emerald-glow">
          <div className="section-divider absolute inset-x-0 top-0" />
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
            <SectionHeading eyebrow="Hall of Fame" title={`Top Agencies · ${ranking.month_label}`} to="/top-agencies" linkText="View rankings" />
            <AgencyPodium agencies={ranking.agencies} />
          </div>
        </section>
      )}

      {/* Resellers preview */}
      {resellers?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-12">
          <SectionHeading eyebrow="Official & Verified" title="Trusted Resellers" to="/resellers" linkText="See all" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {resellers.slice(0, 4).map((r) => <ResellerCard key={r.id} reseller={r} />)}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link to="/resellers" className="inline-flex items-center gap-1 text-sm font-semibold text-gold">
              See all resellers <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
